import {useQuery} from "@tanstack/react-query";
import {Link} from "react-router-dom";
import {Building2, History, RotateCcw, Users, WalletCards} from "lucide-react";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {useTranslation} from "../../shared/i18n";
import {formatMoney, formatNumber} from "../../shared/utils/format";
import {getDeployerReferrals} from "./api";

const emptyPayload = {
    summary: {
        places_with_ref_code: 0,
        invited_users_count: 0,
        referrals_count: 0,
        ref_balance_sum: 0,
        deployer_earned: 0,
        commission_percent: 0,
    },
    results: [],
};

export const DeployerReferralWorkspace = () => {
    const {t} = useTranslation();
    const query = useQuery({
        queryKey: ["deployer-referrals"],
        queryFn: getDeployerReferrals,
        retry: false,
    });
    const payload = query.data || emptyPayload;
    const summary = payload.summary || emptyPayload.summary;
    const rows = payload.results || [];

    const columns = [
        {
            key: "place_name",
            label: t("sections.referrals.deployer.place"),
            render: (row) => (
                <Link to={`/employers/${row.place_id}`}>{row.place_name || `#${row.place_id}`}</Link>
            ),
        },
        {key: "ref_code", label: t("sections.referrals.deployer.code"), render: (row) => row.ref_code || "—"},
        {
            key: "generation_1_count",
            label: t("sections.referrals.deployer.gen1"),
            render: (row) => formatNumber(row.generation_1_count),
        },
        {
            key: "generation_2_count",
            label: t("sections.referrals.deployer.gen2"),
            render: (row) => formatNumber(row.generation_2_count),
        },
        {
            key: "generation_3_count",
            label: t("sections.referrals.deployer.gen3"),
            render: (row) => formatNumber(row.generation_3_count),
        },
        {
            key: "invited_users_count",
            label: t("sections.referrals.deployer.invited"),
            render: (row) => formatNumber(row.invited_users_count),
        },
        {
            key: "referrals_count",
            label: t("sections.referrals.deployer.accruals"),
            render: (row) => formatNumber(row.referrals_count),
        },
        {
            key: "ref_balance_sum",
            label: t("sections.referrals.deployer.balance"),
            render: (row) => <strong>{formatMoney(row.ref_balance_sum)}</strong>,
        },
        {
            key: "commission_percent",
            label: t("sections.referrals.deployer.percent"),
            render: (row) => `${formatNumber(row.commission_percent)}%`,
        },
        {
            key: "deployer_earned",
            label: t("sections.referrals.deployer.earned"),
            render: (row) => <strong>{formatMoney(row.deployer_earned)}</strong>,
        },
    ];

    if (query.isError) {
        return (
            <div className="admin-page">
                <DataState
                    title={t("sections.referrals.deployer.errorTitle")}
                    copy={t("sections.referrals.deployer.errorCopy")}
                />
                <button className="admin-button admin-button-secondary" type="button" onClick={() => query.refetch()}>
                    <RotateCcw size={16} /> {t("sections.referrals.deployer.retry")}
                </button>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <header className="admin-page-header">
                <div>
                    <p className="admin-eyebrow">{t("sections.referrals.deployer.eyebrow")}</p>
                    <h1 className="admin-page-title">{t("sections.referrals.deployer.title")}</h1>
                    <p className="admin-page-copy">{t("sections.referrals.deployer.copy")}</p>
                </div>
            </header>
            <section className="admin-grid cols-4">
                <article className="admin-card">
                    <p className="admin-card-title"><WalletCards size={14} /> {t("sections.referrals.deployer.balance")}</p>
                    <p className="admin-card-value">{formatMoney(summary.ref_balance_sum)}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><History size={14} /> {t("sections.referrals.deployer.earned")}</p>
                    <p className="admin-card-value">{formatMoney(summary.deployer_earned)}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><Users size={14} /> {t("sections.referrals.deployer.invited")}</p>
                    <p className="admin-card-value">{formatNumber(summary.invited_users_count)}</p>
                </article>
                <article className="admin-card">
                    <p className="admin-card-title"><Building2 size={14} /> {t("sections.referrals.deployer.places")}</p>
                    <p className="admin-card-value">{formatNumber(summary.places_with_ref_code)}</p>
                </article>
            </section>
            <AdminTable
                columns={columns}
                rows={rows}
                rowKey="place_id"
                loading={query.isLoading}
                emptyText={t("sections.referrals.deployer.empty")}
            />
        </div>
    );
};
